import type { DocSection } from '../types'

export const usersSection: DocSection = {
  id: 'utilisateurs',
  title: 'Utilisateurs & rôles',
  icon: 'user-plus',
  accent: '#7C3AED',
  summary: "Créer des comptes, attribuer les rôles, configurer le poste téléphonique, fixer les heures de travail.",
  minRole: 'team_admin',
  articles: [
    {
      id: 'creer',
      title: 'Créer un utilisateur',
      summary: 'Compte, équipe, rôle, poste téléphonique.',
      icon: 'user-plus',
      blocks: [
        { type: 'steps', items: [
          "Menu **Utilisateurs** → **Nouvel utilisateur**.",
          "Renseignez **identité**, **identifiant**, **e-mail**, et l'**équipe**.",
          "Choisissez le **rôle** (voir [[utilisateurs/roles|Rôles & permissions]]).",
          "Attribuez une **extension** (numéro de poste interne) — le **poste téléphonique** est préparé automatiquement pour que l'agent puisse appeler.",
          "Enregistrez : l'agent peut se connecter et son softphone s'enregistre automatiquement.",
        ] },
        { type: 'callout', variant: 'info', title: 'Qui peut créer qui', text: "Un **Superviseur** gère les utilisateurs de **son équipe**. Créer un **Administrateur** est réservé à Admin/Propriétaire ; gérer les **Administrateurs** est réservé au **Propriétaire**." },
        { type: 'callout', variant: 'tip', title: 'Case « Autoriser la bascule vers le SIP par défaut »', text: "Visible pour Admin/Propriétaire, sous les lignes assignées. Cochée, elle autorise l'agent à **basculer automatiquement** sur le **SIP par défaut** (ligne de secours) si sa ligne habituelle **tombe en panne** : ses prochains appels partent par cette ligne, avec un des **numéros en rotation** définis pour elle dans **Téléphonie → Réglages** (sinon le numéro affiché par défaut). Non cochée, ses appels échouent tant que sa ligne est en panne. Un petit tag « secours ✓ » apparaît sous le nom SIP dans la liste. Voir [[telephonie/trunks|Opérateurs]]." },
      ],
    },
    {
      id: 'roles',
      title: 'Rôles & permissions',
      summary: 'Ce que chaque rôle peut faire.',
      icon: 'shield',
      blocks: [
        { type: 'p', text: "Quatre rôles, chacun héritant du précédent :" },
        { type: 'table', headers: ['Rôle', 'Peut notamment'], rows: [
          ['**Agent**', "Appeler, qualifier, gérer ses rappels/RDV, voir son historique."],
          ['**Superviseur**', "Tout l'agent + campagnes, supervision live, temps agents, stats, utilisateurs de son équipe."],
          ['**Administrateur**', "Tout le superviseur (sur tout le compte) + téléphonie, liste noire, listes de numéros, distribution, automatisations."],
          ['**Propriétaire**', "Tout + gestion des administrateurs + documentation technique (API)."],
        ] },
        { type: 'callout', variant: 'info', title: 'Documentation API', text: "La documentation technique de l'API (Swagger) est réservée au **Propriétaire**." },
      ],
    },
    {
      id: 'horaires',
      title: 'Heures de travail',
      summary: "Limiter l'accès d'un agent à une plage et des jours.",
      icon: 'clock',
      blocks: [
        { type: 'p', text: "Vous pouvez définir, par **utilisateur** ou par **équipe**, une **plage horaire** et des **jours autorisés**. **Hors de cette plage** :" },
        { type: 'list', items: [
          "La **connexion est refusée**.",
          "Une **session déjà ouverte est fermée** (révocation).",
        ] },
        { type: 'callout', variant: 'info', text: "Les **Propriétaire/Administrateur** sont **exemptés** de cette restriction." },
      ],
    },
    {
      id: 'securite',
      title: 'Sécurité de session',
      summary: 'Un seul appareil par compte.',
      icon: 'shield',
      blocks: [
        { type: 'p', text: "PexiTalk applique le **mono-session** : **un seul appareil connecté à la fois** par compte. Se connecter sur un nouvel appareil **déconnecte** automatiquement la session précédente." },
        { type: 'callout', variant: 'tip', text: "Cela évite les comptes partagés et garantit un journal de temps fiable (un agent = une session active)." },
      ],
    },
  ],
}
